import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, toast } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import type { IdentityProviderOut } from "../shared/types";
import { useAudiences } from "../shared/use-shared-queries";
import type { CreatableProvider } from "./audience-meta";
import { DefaultAudiences, EntraFields, GroupFields, JwtFields } from "./provider-fields";
import { missingRequired, providerBodies, usesGroups, usesJwt, valuesFrom, type ProviderValues } from "./provider-model";
import { useSaveProvider } from "./use-audiences-data";

const FORM_ID = "identity-provider-form";
const NAME_MAX = 120;

interface Props {
  type: CreatableProvider;
  provider: IdentityProviderOut | null;
  onClose: () => void;
}

function useSchema(type: CreatableProvider, hasSecret: boolean) {
  const { t } = useTranslation();
  return z
    .object({ name: z.string().trim().min(1, t("deployKnowledge.audiences.idp.nameRequired")).max(NAME_MAX) })
    .passthrough()
    .superRefine((values, ctx) => {
      const missing = missingRequired(type, values as unknown as ProviderValues, hasSecret);
      if (missing) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["root"], message: t(`deployKnowledge.audiences.idp.${missing}`) });
    }) as unknown as z.ZodType<ProviderValues>;
}

/** Create / configure one identity provider (`POST` or `PATCH /v1/identity-providers`). */
export function ProviderDialog({ type, provider, onClose }: Props) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const audiences = useAudiences();
  const save = useSaveProvider();
  const typeName = t(`deployKnowledge.audiences.idp.types.${type}`);
  const { register, control, handleSubmit, formState } = useForm<ProviderValues>({
    resolver: zodResolver(useSchema(type, provider?.has_secret ?? false)),
    defaultValues: valuesFrom(provider, typeName),
  });
  const submit = handleSubmit((values) => {
    save.mutate(
      { id: provider?.id, ...providerBodies(type, values) },
      {
        onSuccess: () => {
          toast.success(t("deployKnowledge.audiences.idp.saved", { name: values.name.trim() }));
          onClose();
        },
        onError: (caught) => toast.error(message(caught)),
      },
    );
  });
  const list = audiences.data ?? [];
  const rootError = (formState.errors as { root?: { message?: string } }).root?.message;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-xl"
        title={provider ? t("deployKnowledge.audiences.idp.configureTitle", { name: provider.name }) : t("deployKnowledge.audiences.idp.connectTitle", { type: typeName })}
        description={t(`deployKnowledge.audiences.idp.descriptions.${type}`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" form={FORM_ID} loading={save.isPending}>
              {provider ? t("common.save") : t("deployKnowledge.audiences.idp.connect")}
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className="space-y-4" noValidate onSubmit={(event) => void submit(event)}>
          <Field label={t("deployKnowledge.audiences.idp.name")} error={formState.errors.name?.message}>
            <Input maxLength={NAME_MAX} {...register("name")} />
          </Field>
          {usesJwt(type) ? <JwtFields control={control} register={register} audiences={list} /> : null}
          {type === "google_workspace" ? (
            <Field label={t("deployKnowledge.audiences.idp.domain")}>
              <Input className="font-mono text-xs" placeholder="example.com" {...register("domain")} />
            </Field>
          ) : null}
          {type === "microsoft_entra" ? <EntraFields register={register} redirectUri={provider?.redirect_uri ?? null} /> : null}
          {usesGroups(type) ? <GroupFields control={control} register={register} audiences={list} /> : null}
          <DefaultAudiences control={control} audiences={list} />
          {rootError ? (
            <p role="alert" className="text-sm text-danger">
              {rootError}
            </p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
